/**
 * Identity API client for the SPA. Every request carries the CSRF token
 * (read from the `velora_csrf` cookie, so it is always the current one) and
 * the office selector header. Other modules and the shell should build
 * their client with `identityHeaders` (see docs/requests/wp0-auth-shell-integration.md).
 */
import { OFFICE_HEADER, type LocationScope } from '@velora/contracts';
import {
  AnnouncementListResponse,
  AnnouncementSchema,
  SupportFeedbackDetailSchema,
  SupportFeedbackListResponse,
  type AnnouncementSaveBody,
  type FeedbackPriority,
  type FeedbackStatus,
} from '@velora/contracts/feedback';
import {
  BACKGROUND_HEADER,
  COOKIE_CSRF,
  CSRF_HEADER,
  DeviceSchema,
  EndImpersonationResponseSchema,
  ForgetDevicesResponseSchema,
  InviteEmailCodeResponseSchema,
  LoginResponseSchema,
  MfaConfirmResponseSchema,
  MfaEnrollmentSchema,
  MfaSendResponseSchema,
  MfaSetupOptionsSchema,
  MfaSignInOptionsSchema,
  MfaStatusSchema,
  OfficeContextSchema,
  OfficeSchema,
  OkSchema,
  PasswordSetupPeekSchema,
  PasswordSetupResultSchema,
  InviteLinkSchema,
  InviteUserResponseSchema,
  PmsStaffRosterSchema,
  type InviteUserRequest,
  type ResendInviteRequest,
  ResetPasswordResponseSchema,
  type ResetPasswordRequest,
  type SupportResetPasswordRequest,
  PermissionCatalogSchema,
  PracticeUserSchema,
  SupportC2gUserResponseSchema,
  SupportFoundUserSchema,
  RememberedDeviceListSchema,
  RoleSchema,
  SessionInfoSchema,
  SessionPeekSchema,
  SupportAccessEntrySchema,
  SupportAuditEventSchema,
  SupportDashboardSchema,
  type SupportReplacesRequest,
  SupportNoteSchema,
  SupportPracticeSummarySchema,
  SupportUserSchema,
  type SupportUpdateMemberRequest,
  SupportCreateMemberResponseSchema,
  SupportCreatePracticeResponseSchema,
  SupportImpersonateResponseSchema,
  SupportMemberSchema,
  SupportOfficeStatusListSchema,
  SupportPracticeSchema,
  SupportRoleSchema,
  type SupportAddOfficeRequest,
  type SupportAssignPulsesyncRequest,
  type SupportCreateMemberRequest,
  type SupportCreatePracticeRequest,
  type SupportImpersonateRequest,
  type CreateOfficeRequest,
  type CreateRoleRequest,
  type MfaMethodKind,
  type MfaSentMethod,
  type CreateUserRequest,
  type EnrollDeviceRequest,
  type LoginRequest,
  type UpdateOfficeContext,
  type UpdateOfficeRequest,
  type UpdateRoleRequest,
  type UpdateUserRequest,
} from '@velora/contracts/identity';
import type { LinkOffice } from '@velora/contracts/settings';
import type { SupportUsageBillingRequest } from '@velora/contracts/usage-billing';
import { scopeToParam } from '@velora/domain/tenancy';
import { ApiError, createHttpClient } from '@velora/sdk';
import { z } from 'zod';

export { ApiError };

export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    if (part.slice(0, eq).trim() === name) {
      try {
        return decodeURIComponent(part.slice(eq + 1).trim());
      } catch {
        return part.slice(eq + 1).trim();
      }
    }
  }
  return null;
}

let currentScope: LocationScope | null = null;

/** Called by the session provider whenever the office selection changes. */
export function setRequestScope(scope: LocationScope | null): void {
  currentScope = scope;
}

/**
 * Headers every Velora API request should send: the CSRF token and the
 * office selector. Pass to `createHttpClient({ headers: identityHeaders })`.
 */
export function identityHeaders(): Record<string, string> {
  const h: Record<string, string> = {};
  const csrf = readCookie(COOKIE_CSRF);
  if (csrf) h[CSRF_HEADER] = csrf;
  if (currentScope) h[OFFICE_HEADER] = scopeToParam(currentScope);
  return h;
}

/** Resolves `fetch` per call (not at import), so tests and polyfills can replace it. */
const lateFetch: typeof fetch = (input, init) => globalThis.fetch(input, init);

export const identityClient = createHttpClient({
  baseUrl: '/v1',
  headers: identityHeaders,
  fetch: lateFetch,
});

const background = { [BACKGROUND_HEADER]: '1' };
const backgroundClient = createHttpClient({
  baseUrl: '/v1',
  fetch: lateFetch,
  headers: () => ({ ...identityHeaders(), ...background }),
});

/** The refusal reason of a 401/403 (`error.details.reason`), if any. */
export function refusalReason(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const d = err.details;
  if (d && typeof d === 'object' && 'reason' in d && typeof d.reason === 'string') return d.reason;
  return null;
}

/** The human message of an API error, or a fallback. */
export function errorMessage(err: unknown, fallback = 'Something went wrong. Try again.'): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: unknown } } | undefined;
    const m = body?.error?.message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return fallback;
}

export function attemptsLeft(err: unknown): number | null {
  if (!(err instanceof ApiError)) return null;
  const d = err.details as { attemptsLeft?: unknown } | undefined;
  return typeof d?.attemptsLeft === 'number' ? d.attemptsLeft : null;
}

const c = identityClient;
const UsersList = z.object({ users: z.array(PracticeUserSchema) });
const RolesList = z.object({ roles: z.array(RoleSchema) });
const OfficesList = z.object({ offices: z.array(OfficeSchema) });
const DevicesList = z.object({ devices: z.array(DeviceSchema) });
const SignOutResult = z.object({ sessionsEnded: z.number() });

const OfficeSystemsSchema = z.object({
  offices: z.array(z.object({ id: z.string(), pmsSystem: z.string().nullable() })),
});

/** PMS names as the top-bar office switcher shows them. */
export const PMS_SYSTEM_LABEL: Record<string, string> = {
  opendental: 'Open Dental',
  dentrix: 'Dentrix',
  eaglesoft: 'Eaglesoft',
};

/** "Open Dental via PulseSync" or "Velora native" for an office. */
export function officeSystemLabel(pmsSystem: string | null): string {
  return pmsSystem ? `${PMS_SYSTEM_LABEL[pmsSystem] ?? pmsSystem} via PulseSync` : 'Velora native';
}

export const identityApi = {
  /** Each office's PMS, for the sign-in office step (same words as the switcher). */
  officeSystems: () => c.request('/shell/bootstrap', { schema: OfficeSystemsSchema }),
  peek: (signal?: AbortSignal) =>
    backgroundClient.request('/identity/session/peek', {
      schema: SessionPeekSchema,
      ...(signal ? { signal } : {}),
    }),
  session: (signal?: AbortSignal) =>
    backgroundClient.request('/identity/session', {
      schema: SessionInfoSchema,
      ...(signal ? { signal } : {}),
    }),
  login: (body: LoginRequest) =>
    c.request('/identity/login', { method: 'POST', body, schema: LoginResponseSchema }),
  /**
   * `rememberDevice`: the "Remember this device for 30 days" box. `method`:
   * which code this is (absent: authenticator or recovery code, by shape).
   */
  verifyMfa: (
    code: string,
    rememberDevice = false,
    method?: 'totp' | 'sms' | 'email' | 'recovery',
  ) =>
    c.request('/identity/login/mfa', {
      method: 'POST',
      body: {
        code,
        ...(rememberDevice ? { rememberDevice: true } : {}),
        ...(method ? { method } : {}),
      },
      schema: LoginResponseSchema,
    }),
  /** The code step: the default method and "Try another way". */
  mfaOptions: () => c.request('/identity/login/mfa/options', { schema: MfaSignInOptionsSchema }),
  /** Texts or emails a sign-in code. */
  sendMfaCode: (method: MfaSentMethod) =>
    c.request('/identity/login/mfa/send', {
      method: 'POST',
      body: { method },
      schema: MfaSendResponseSchema,
    }),
  /** Two-step set-up at sign-in: the methods this person may choose. */
  setupOptions: () => c.request('/identity/login/mfa/setup', { schema: MfaSetupOptionsSchema }),
  setupContact: (kind: MfaSentMethod, destination: string) =>
    c.request('/identity/login/mfa/setup/contact', {
      method: 'POST',
      body: { kind, destination },
      schema: MfaSendResponseSchema,
    }),
  setupContactResend: (method: MfaSentMethod) =>
    c.request('/identity/login/mfa/setup/contact/resend', {
      method: 'POST',
      body: { method },
      schema: MfaSendResponseSchema,
    }),
  setupContactConfirm: (kind: MfaSentMethod, code: string) =>
    c.request('/identity/login/mfa/setup/contact/confirm', {
      method: 'POST',
      body: { kind, code },
      schema: MfaConfirmResponseSchema,
    }),
  /** Shows the pending code again; `restart` throws it away and makes a new one. */
  startEnrollment: (restart = false) =>
    c.request('/identity/login/mfa/enroll', {
      method: 'POST',
      ...(restart ? { body: { restart: true } } : {}),
      schema: MfaEnrollmentSchema,
    }),
  confirmEnrollment: (code: string) =>
    c.request('/identity/login/mfa/enroll/confirm', {
      method: 'POST',
      body: { code },
      schema: MfaConfirmResponseSchema,
    }),
  /** First sign-in with a temporary password: the temporary one, then the person's own. */
  firstSignInPassword: (currentPassword: string, newPassword: string) =>
    c.request('/identity/login/first/password', {
      method: 'POST',
      body: { currentPassword, newPassword },
      schema: LoginResponseSchema,
    }),
  /** First sign-in: the person's own email in place of the placeholder. */
  firstSignInEmail: (email: string) =>
    c.request('/identity/login/first/email', {
      method: 'POST',
      body: { email },
      schema: LoginResponseSchema,
    }),
  unlock: (body: { pin: string } | { password: string }) =>
    c.request('/identity/session/unlock', { method: 'POST', body, schema: OkSchema }),
  lock: () => c.request('/identity/session/lock', { method: 'POST', schema: OkSchema }),
  logout: () => c.request('/identity/logout', { method: 'POST', schema: OkSchema }),
  acceptImpersonation: (token: string) =>
    c.request('/identity/impersonation/accept', {
      method: 'POST',
      body: { token },
      schema: LoginResponseSchema,
    }),
  /** "Return to my account": ends the support session; `restored` when the support account's own session is back. */
  endImpersonation: () =>
    c.request('/identity/impersonation/end', {
      method: 'POST',
      schema: EndImpersonationResponseSchema,
    }),
  passwordSetupCheck: (token: string) =>
    c.request('/identity/password-setup/check', {
      method: 'POST',
      body: { token },
      schema: PasswordSetupPeekSchema,
    }),
  passwordSetup: (token: string, password: string, email?: string, emailCode?: string) =>
    c.request('/identity/password-setup', {
      method: 'POST',
      body: {
        token,
        password,
        ...(email ? { email } : {}),
        ...(emailCode ? { emailCode } : {}),
      },
      schema: PasswordSetupResultSchema,
    }),
  /** First sign-in: email a 6-digit code to the person's own email. */
  passwordSetupEmailCode: (token: string, email: string) =>
    c.request('/identity/password-setup/email-code', {
      method: 'POST',
      body: { token, email },
      schema: InviteEmailCodeResponseSchema,
    }),
  supportPractices: () =>
    c.request('/identity/support/practices', {
      schema: z.object({ practices: z.array(SupportPracticeSchema) }),
    }),
  supportMembers: (practiceId: string) =>
    c.request(`/identity/support/practices/${encodeURIComponent(practiceId)}/members`, {
      schema: z.object({ members: z.array(SupportMemberSchema) }),
    }),
  supportAccessLog: () =>
    c.request('/identity/support/access-log', {
      schema: z.object({ entries: z.array(SupportAccessEntrySchema) }),
    }),
  supportOffices: (practiceId: string) =>
    c.request(`/identity/support/practices/${encodeURIComponent(practiceId)}/offices`, {
      schema: SupportOfficeStatusListSchema,
    }),
  supportRoles: (practiceId: string) =>
    c.request(`/identity/support/practices/${encodeURIComponent(practiceId)}/roles`, {
      schema: z.object({ roles: z.array(SupportRoleSchema) }),
    }),
  supportCreateMember: (practiceId: string, body: SupportCreateMemberRequest) =>
    c.request(`/identity/support/practices/${encodeURIComponent(practiceId)}/members`, {
      method: 'POST',
      body,
      schema: SupportCreateMemberResponseSchema,
    }),
  supportCreatePractice: (body: SupportCreatePracticeRequest) =>
    c.request('/identity/support/practices', {
      method: 'POST',
      body,
      schema: SupportCreatePracticeResponseSchema,
    }),
  supportAddOffice: (practiceId: string, body: SupportAddOfficeRequest) =>
    c.request(`/identity/support/practices/${encodeURIComponent(practiceId)}/offices`, {
      method: 'POST',
      body,
      schema: z.object({ id: z.uuid() }),
    }),
  supportAssignPulsesync: (practiceId: string, body: SupportAssignPulsesyncRequest) =>
    c.request(`/identity/support/practices/${encodeURIComponent(practiceId)}/pulsesync-offices`, {
      method: 'POST',
      body,
      schema: z.object({ assigned: z.boolean() }),
    }),
  supportUnassignPulsesync: (practiceId: string, officeId: string) =>
    c.request(
      `/identity/support/practices/${encodeURIComponent(practiceId)}/pulsesync-offices/${encodeURIComponent(officeId)}`,
      { method: 'DELETE', schema: z.object({ removed: z.boolean() }) },
    ),
  supportLinkOffice: (practiceId: string, locationId: string, body: LinkOffice) =>
    c.request(
      `/identity/support/practices/${encodeURIComponent(practiceId)}/offices/${encodeURIComponent(locationId)}/link`,
      { method: 'POST', body, schema: z.object({ linkId: z.uuid() }) },
    ),
  supportDashboard: () =>
    c.request('/identity/support/dashboard', { schema: SupportDashboardSchema }),
  supportSummary: (practiceId: string) =>
    c.request(`/identity/support/practices/${encodeURIComponent(practiceId)}/summary`, {
      schema: SupportPracticeSummarySchema,
    }),
  supportUsers: (practiceId: string) =>
    c.request(`/identity/support/practices/${encodeURIComponent(practiceId)}/users`, {
      schema: z.object({ users: z.array(SupportUserSchema) }),
    }),
  /** The practice's own Computers2Go Support user (created when missing, audited). */
  supportEnsureC2gUser: (practiceId: string) =>
    c.request(`/identity/support/practices/${encodeURIComponent(practiceId)}/c2g-support-user`, {
      method: 'POST',
      body: {},
      schema: SupportC2gUserResponseSchema,
    }),
  /** Members of every practice matching a name, email, role or office. */
  supportFindUsers: (q: string) =>
    c.request(`/identity/support/users?q=${encodeURIComponent(q)}`, {
      schema: z.object({ users: z.array(SupportFoundUserSchema) }),
    }),
  supportUpdateUser: (practiceId: string, userId: string, body: SupportUpdateMemberRequest) =>
    c.request(
      `/identity/support/practices/${encodeURIComponent(practiceId)}/users/${encodeURIComponent(userId)}`,
      { method: 'PATCH', body, schema: z.object({ ok: z.literal(true) }) },
    ),
  /** Unlock or two-step reset: account-wide, so a reason is required. */
  supportUserAction: (
    practiceId: string,
    userId: string,
    action: 'unlock' | 'reset-mfa',
    reason: string,
  ) =>
    c.request(
      `/identity/support/practices/${encodeURIComponent(practiceId)}/users/${encodeURIComponent(userId)}/${action}`,
      {
        method: 'POST',
        body: { reason },
        schema: z.object({ changed: z.boolean(), practices: z.number().int() }),
      },
    ),
  /** C2G Admin: reset a set-up person's password (account-wide, with a reason). */
  supportResetPassword: (practiceId: string, userId: string, body: SupportResetPasswordRequest) =>
    c.request(
      `/identity/support/practices/${encodeURIComponent(practiceId)}/users/${encodeURIComponent(userId)}/reset-password`,
      { method: 'POST', body, schema: ResetPasswordResponseSchema },
    ),
  /** A new one-time set-up link for someone with no password (burns the old one). */
  supportInvite: (
    practiceId: string,
    userId: string,
    body: { action: 'link' | 'email'; reason: string },
  ) =>
    c.request(
      `/identity/support/practices/${encodeURIComponent(practiceId)}/users/${encodeURIComponent(userId)}/invite`,
      { method: 'POST', body, schema: InviteLinkSchema },
    ),
  /** The real email of someone who has not set up yet. */
  supportSetEmail: (practiceId: string, userId: string, body: { email: string; reason: string }) =>
    c.request(
      `/identity/support/practices/${encodeURIComponent(practiceId)}/users/${encodeURIComponent(userId)}/email`,
      {
        method: 'PUT',
        body,
        schema: z.object({ ok: z.literal(true), practices: z.number().int() }),
      },
    ),
  supportAudit: (practiceId: string) =>
    c.request(`/identity/support/practices/${encodeURIComponent(practiceId)}/audit`, {
      schema: z.object({ events: z.array(SupportAuditEventSchema) }),
    }),
  supportNotes: (practiceId: string) =>
    c.request(`/identity/support/practices/${encodeURIComponent(practiceId)}/notes`, {
      schema: z.object({ notes: z.array(SupportNoteSchema) }),
    }),
  supportAddNote: (practiceId: string, body: string) =>
    c.request(`/identity/support/practices/${encodeURIComponent(practiceId)}/notes`, {
      method: 'POST',
      body: { body },
      schema: z.object({ id: z.uuid() }),
    }),
  supportFeedback: (f: {
    status: FeedbackStatus | 'active' | 'all';
    practiceId: string | null;
  }) => {
    const q = new URLSearchParams({ status: f.status });
    if (f.practiceId) q.set('practiceId', f.practiceId);
    return c.request(`/identity/support/feedback?${q.toString()}`, {
      schema: SupportFeedbackListResponse,
    });
  },
  supportFeedbackDetail: (id: string) =>
    c.request(`/identity/support/feedback/${encodeURIComponent(id)}`, {
      schema: SupportFeedbackDetailSchema,
    }),
  supportFeedbackSetStatus: (
    id: string,
    body: { status: FeedbackStatus; priority: FeedbackPriority },
  ) =>
    c.request(`/identity/support/feedback/${encodeURIComponent(id)}/status`, {
      method: 'POST',
      body,
      schema: SupportFeedbackDetailSchema,
    }),
  supportAnnouncements: () =>
    c.request('/identity/support/announcements', { schema: AnnouncementListResponse }),
  supportSaveAnnouncement: (id: string | null, body: AnnouncementSaveBody) =>
    c.request(
      id
        ? `/identity/support/announcements/${encodeURIComponent(id)}`
        : '/identity/support/announcements',
      { method: id ? 'PUT' : 'POST', body, schema: AnnouncementSchema },
    ),
  supportDeleteNote: (practiceId: string, noteId: string) =>
    c.request(
      `/identity/support/practices/${encodeURIComponent(practiceId)}/notes/${encodeURIComponent(noteId)}`,
      { method: 'DELETE', schema: z.object({ removed: z.boolean() }) },
    ),
  /** What Velora replaces for the practice (support's billing note; never charges). */
  supportSetReplaces: (practiceId: string, body: SupportReplacesRequest) =>
    c.request(`/identity/support/practices/${encodeURIComponent(practiceId)}/replaces`, {
      method: 'PUT',
      body,
      schema: z.object({ ok: z.literal(true) }),
    }),
  /** The per-practice usage-billing switch (default off; records the decision only). */
  supportSetUsageBilling: (practiceId: string, body: SupportUsageBillingRequest) =>
    c.request(`/identity/support/practices/${encodeURIComponent(practiceId)}/usage-billing`, {
      method: 'PUT',
      body,
      schema: z.object({ changed: z.boolean() }),
    }),
  /** Signs a one-shot support link on the server (the secret never reaches the browser). */
  supportImpersonate: (body: SupportImpersonateRequest) =>
    c.request('/identity/support/impersonate', {
      method: 'POST',
      body,
      schema: SupportImpersonateResponseSchema,
    }),
  context: () => c.request('/identity/context', { schema: OfficeContextSchema }),
  updateContext: (body: UpdateOfficeContext) =>
    c.request('/identity/context', { method: 'PUT', body, schema: OfficeContextSchema }),

  mfaStatus: () => c.request('/identity/me/mfa', { schema: MfaStatusSchema }),
  enrollMfa: (restart = false) =>
    c.request('/identity/me/mfa/enroll', {
      method: 'POST',
      ...(restart ? { body: { restart: true } } : {}),
      schema: MfaEnrollmentSchema,
    }),
  confirmMfa: (code: string) =>
    c.request('/identity/me/mfa/confirm', {
      method: 'POST',
      body: { code },
      schema: MfaConfirmResponseSchema,
    }),
  /** My sign-in: prove a mobile number or an email (a code is sent). */
  startMfaContact: (kind: MfaSentMethod, destination: string) =>
    c.request('/identity/me/mfa/contact', {
      method: 'POST',
      body: { kind, destination },
      schema: MfaSendResponseSchema,
    }),
  resendMfaContact: (method: MfaSentMethod) =>
    c.request('/identity/me/mfa/contact/resend', {
      method: 'POST',
      body: { method },
      schema: MfaSendResponseSchema,
    }),
  confirmMfaContact: (kind: MfaSentMethod, code: string) =>
    c.request('/identity/me/mfa/contact/confirm', {
      method: 'POST',
      body: { kind, code },
      schema: MfaConfirmResponseSchema,
    }),
  removeMfaMethod: (kind: MfaMethodKind, password: string) =>
    c.request(`/identity/me/mfa/methods/${kind}`, {
      method: 'DELETE',
      body: { password },
      schema: OkSchema,
    }),
  setDefaultMfaMethod: (method: MfaMethodKind) =>
    c.request('/identity/me/mfa/default', { method: 'PUT', body: { method }, schema: OkSchema }),
  disableMfa: (password: string) =>
    c.request('/identity/me/mfa', { method: 'DELETE', body: { password }, schema: OkSchema }),
  setPin: (pin: string, password: string) =>
    c.request('/identity/me/pin', { method: 'PUT', body: { pin, password }, schema: OkSchema }),
  removePin: () => c.request('/identity/me/pin', { method: 'DELETE', schema: OkSchema }),
  rememberedDevices: () =>
    c.request('/identity/me/remembered-devices', { schema: RememberedDeviceListSchema }),
  forgetRememberedDevice: (id: string) =>
    c.request(`/identity/me/remembered-devices/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      schema: ForgetDevicesResponseSchema,
    }),
  forgetAllRememberedDevices: () =>
    c.request('/identity/me/remembered-devices', {
      method: 'DELETE',
      schema: ForgetDevicesResponseSchema,
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    c.request('/identity/me/password', {
      method: 'POST',
      body: { currentPassword, newPassword },
      schema: OkSchema,
    }),

  users: () => c.request('/identity/users', { schema: UsersList }),
  /** W12: add a person with no password and get their one-time set-up link. */
  inviteUser: (body: InviteUserRequest) =>
    c.request('/identity/users/invite', {
      method: 'POST',
      body,
      schema: InviteUserResponseSchema,
    }),
  /** W12: a new link for someone who has not set up yet (copy it, or email it). */
  resendInvite: (id: string, body: ResendInviteRequest) =>
    c.request(`/identity/users/${encodeURIComponent(id)}/invite`, {
      method: 'POST',
      body,
      schema: InviteLinkSchema,
    }),
  /** W12: active people in the offices' PMS user lists, one per person. */
  pmsStaff: () => c.request('/identity/users/pms-staff', { schema: PmsStaffRosterSchema }),
  createUser: (body: CreateUserRequest) =>
    c.request('/identity/users', { method: 'POST', body, schema: PracticeUserSchema }),
  /** Change (or remove, with null) a person's username. Audited. */
  setUsername: (id: string, username: string | null) =>
    c.request(`/identity/users/${encodeURIComponent(id)}/username`, {
      method: 'PUT',
      body: { username },
      schema: PracticeUserSchema,
    }),
  updateUser: (id: string, body: UpdateUserRequest) =>
    c.request(`/identity/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
      schema: PracticeUserSchema,
    }),
  signOutUser: (id: string) =>
    c.request(`/identity/users/${encodeURIComponent(id)}/sign-out`, {
      method: 'POST',
      schema: SignOutResult,
    }),
  unlockUser: (id: string) =>
    c.request(`/identity/users/${encodeURIComponent(id)}/unlock`, {
      method: 'POST',
      schema: OkSchema,
    }),
  resetUserMfa: (id: string) =>
    c.request(`/identity/users/${encodeURIComponent(id)}/reset-mfa`, {
      method: 'POST',
      schema: OkSchema,
    }),
  /** Reset a set-up person's password: a one-time link (copy or email) or a temporary password. */
  resetUserPassword: (id: string, body: ResetPasswordRequest) =>
    c.request(`/identity/users/${encodeURIComponent(id)}/reset-password`, {
      method: 'POST',
      body,
      schema: ResetPasswordResponseSchema,
    }),
  forgetUserDevices: (id: string) =>
    c.request(`/identity/users/${encodeURIComponent(id)}/forget-devices`, {
      method: 'POST',
      schema: ForgetDevicesResponseSchema,
    }),

  permissions: () => c.request('/identity/permissions', { schema: PermissionCatalogSchema }),
  roles: () => c.request('/identity/roles', { schema: RolesList }),
  createRole: (body: CreateRoleRequest) =>
    c.request('/identity/roles', { method: 'POST', body, schema: RoleSchema }),
  updateRole: (id: string, body: UpdateRoleRequest) =>
    c.request(`/identity/roles/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
      schema: RoleSchema,
    }),
  deleteRole: (id: string) =>
    c.request(`/identity/roles/${encodeURIComponent(id)}`, { method: 'DELETE', schema: OkSchema }),

  offices: () => c.request('/identity/offices', { schema: OfficesList }),
  createOffice: (body: CreateOfficeRequest) =>
    c.request('/identity/offices', { method: 'POST', body, schema: OfficeSchema }),
  updateOffice: (id: string, body: UpdateOfficeRequest) =>
    c.request(`/identity/offices/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
      schema: OfficeSchema,
    }),
  retireOffice: (id: string) =>
    c.request(`/identity/offices/${encodeURIComponent(id)}/retire`, {
      method: 'POST',
      schema: OfficeSchema,
    }),
  reopenOffice: (id: string) =>
    c.request(`/identity/offices/${encodeURIComponent(id)}/reopen`, {
      method: 'POST',
      schema: OfficeSchema,
    }),

  devices: () => c.request('/identity/devices', { schema: DevicesList }),
  enrollDevice: (body: EnrollDeviceRequest) =>
    c.request('/identity/devices', { method: 'POST', body, schema: DeviceSchema }),
  revokeDevice: (id: string) =>
    c.request(`/identity/devices/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      schema: OkSchema,
    }),
};

/** Query keys, so SSE invalidation (`membership`, `location`, `practice_role`) can target them. */
export const identityKeys = {
  session: ['identity', 'session'] as const,
  peek: ['identity', 'peek'] as const,
  users: ['identity', 'users'] as const,
  pmsStaff: ['identity', 'users', 'pms-staff'] as const,
  roles: ['identity', 'roles'] as const,
  permissions: ['identity', 'permissions'] as const,
  offices: ['identity', 'offices'] as const,
  devices: ['identity', 'devices'] as const,
  mfa: ['identity', 'mfa'] as const,
  remembered: ['identity', 'remembered-devices'] as const,
};
